import { useEffect, useState } from "react";
import { Shield, Plus, KeyRound, Trash2, Copy, Link2 } from "lucide-react";
import { http, fmtDateTime, errMsg } from "../api";

export default function AdminAccess({ user }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [mine, setMine] = useState({ current_password: "", new_password: "" });
  const [notice, setNotice] = useState(null);
  const load = () => http.get("/admin/access").then(r => setAdmins(r.data));
  useEffect(() => { load(); }, []);
  const say = (tone, text) => setNotice({ tone, text });

  const add = async e => {
    e.preventDefault();
    try { await http.post("/admin/access", form); say("success", `${form.email} now has admin access`); setForm({ email: "", name: "", password: "" }); load(); }
    catch (e) { say("error", errMsg(e)); }
  };
  const setPassword = async () => {
    try { await http.patch(`/admin/access/${pwFor.id}`, { password: newPw }); say("success", `Password updated for ${pwFor.email}`); setPwFor(null); setNewPw(""); }
    catch (e) { say("error", errMsg(e)); }
  };
  const remove = async a => {
    if (!window.confirm(`Remove admin access for ${a.email}?`)) return;
    try { await http.delete(`/admin/access/${a.id}`); say("success", "Access removed"); load(); } catch (e) { say("error", errMsg(e)); }
  };
  const changeMine = async e => {
    e.preventDefault();
    try { await http.post("/auth/change-password", mine); say("success", "Your password has been changed"); setMine({ current_password: "", new_password: "" }); }
    catch (e) { say("error", errMsg(e)); }
  };
  const adminUrl = `${window.location.origin}/admin`;

  return (
    <>
      <div className="page-head">
        <div><h1>Admin access</h1><p>Who can log in to this owner console. Admin login page: <a href={adminUrl}>{adminUrl}</a></p></div>
        <button className="ghost" onClick={() => { navigator.clipboard.writeText(adminUrl); say("success", "Admin login link copied"); }} data-testid="copy-admin-link"><Link2 size={13} /> Copy admin login link</button>
      </div>
      {notice && <div className={`notice ${notice.tone}`} data-testid="access-notice">{notice.text}</div>}
      <div className="dash-grid">
        <div className="dash-main">
          <div className="card">
            <div className="card-head"><h2><Shield size={15} style={{ display: "inline", marginRight: 6 }} />Admins ({admins.length})</h2></div>
            <div className="list-rows">
              {admins.map(a => (
                <div key={a.id} className="list-row" data-testid={`admin-row-${a.id}`}>
                  <div className="list-row-info"><h3>{a.name || a.email} {a.id === user.id && <span className="badge success">you</span>}</h3><p>{a.email} · Last login {a.last_login_at ? fmtDateTime(a.last_login_at) : "—"}</p></div>
                  <div className="list-row-actions">
                    <button className="pill-action neutral" onClick={() => { setPwFor(a); setNewPw(""); }} data-testid={`admin-setpw-${a.id}`}><KeyRound size={13} /> Set password</button>
                    {a.id !== user.id && <button className="pill-action reject" onClick={() => remove(a)} data-testid={`admin-remove-${a.id}`}><Trash2 size={13} /></button>}
                  </div>
                  {pwFor?.id === a.id && (
                    <div style={{ width: "100%", padding: 10, background: "var(--surface-2)", borderRadius: 8 }}>
                      <label>New password for {a.email} (min 8)</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input type="text" value={newPw} onChange={e => setNewPw(e.target.value)} data-testid="admin-newpw" />
                        <button className="primary" onClick={setPassword} disabled={newPw.length < 8} data-testid="admin-newpw-save">Save</button>
                        <button className="ghost" onClick={() => setPwFor(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><h2><Plus size={15} style={{ display: "inline", marginRight: 6 }} />Give admin access to a new email</h2></div>
            <form onSubmit={add}>
              <div className="field-row">
                <div><label>Email *</label><input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="add-admin-email" /></div>
                <div><label>Name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="add-admin-name" /></div>
              </div>
              <div><label>Password * (min 8)</label><input required minLength={8} type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="add-admin-password" /></div>
              <button className="primary" type="submit" style={{ marginTop: 10 }} data-testid="add-admin-submit"><Plus size={14} /> Add admin</button>
            </form>
          </div>
        </div>
        <div className="card">
          <div className="card-head"><h2><KeyRound size={15} style={{ display: "inline", marginRight: 6 }} />Change my password</h2></div>
          <form onSubmit={changeMine}>
            <div><label>Current password</label><input required type="password" value={mine.current_password} onChange={e => setMine({ ...mine, current_password: e.target.value })} data-testid="my-current-pw" /></div>
            <div><label>New password (min 8)</label><input required minLength={8} type="password" value={mine.new_password} onChange={e => setMine({ ...mine, new_password: e.target.value })} data-testid="my-new-pw" /></div>
            <button className="primary" type="submit" style={{ marginTop: 10 }} data-testid="my-pw-save">Update password</button>
          </form>
          <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 12 }}><Copy size={11} style={{ display: "inline" }} /> Tip: share the admin login link only with people listed above.</p>
        </div>
      </div>
    </>
  );
}
